import { makeAutoObservable, runInAction } from "mobx";
import { rootContainer, TYPE } from "../services/container/root";
import MixpanelService, { IMixPanelIdentity, MixpanelOptionalProperties } from "../services/mixpanel.service";
import UserService from "../services/api/user-service";

interface PendingPageView {
    urlPath: string;
    browserUrl: string;
    properties?: MixpanelOptionalProperties;
}

class RmsMixpanelStore {
    private _identity?: IMixPanelIdentity;
    private _properties: Record<string, any> = {};
    private _pendingPageView: PendingPageView | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _mixpanelService(): MixpanelService {
        return rootContainer.get<MixpanelService>(TYPE.MixpanelService);
    }

    private get _userService(): UserService {
        return rootContainer.get<UserService>(TYPE.UserService);
    }

    public setIdentity(identity: IMixPanelIdentity): void {
        this._identity = identity;
        this._properties.shop = identity.shop_name;
    }

    async load() {
        try {
            const userIdentity = await this._userService.getCurrentPortalIdentity();

            runInAction(() => {
                this.setIdentity({
                    shop_id: userIdentity.portal_id,
                    shop_name: userIdentity.portal_name,
                    shop_company: userIdentity.logged_in_user_company,
                    human_id: userIdentity.logged_in_user_id,
                    human_email: userIdentity.logged_in_user_email,
                    human_role: userIdentity.logged_in_user_role,
                });
                this._flushPendingPageView();
            });
        } catch (e) {
            console.error('[RmsMixpanelStore::load] failed:', e);
        }
    }

    public trackEvent(eventName: string, properties?: MixpanelOptionalProperties): void {
        /** wrapped in try catch to make sure it will not break functionality */
        try {
            this._mixpanelService.trackEvent(eventName, { ...this._properties, ...properties, event_timestamp: Date.now() }, { ...this._identity });
        }
        catch (e) {
            console.error('[RmsMixpanelStore::trackEvent] failed:', e);
        }
    }

    /**
     * Tracks a page view with the browser URL (Laravel path) for Mixpanel analytics.
     * Replaces Mixpanel's automatic page view tracking which used the iframe's internal RMS path.
     * If identity is not yet loaded (e.g. on initial page load), the page view is queued and sent when load() completes.
     * @param urlPath - Path with query (e.g. /dashboard/returns?rmaid=182) for event properties
     * @param browserUrl - Full URL (e.g. https://app.returngo.ai/dashboard/returns) for Mixpanel $current_url (heatmaps)
     * @param properties
     */
    public trackPageView(urlPath: string, browserUrl: string, properties?: MixpanelOptionalProperties): void {
        try {
            if (!this._identity) {
                this._pendingPageView = { urlPath, browserUrl: browserUrl, properties };
                return;
            }
            this._sendPageView(urlPath, browserUrl, properties);
        } catch (e) {
            console.error('[RmsMixpanelStore::trackPageView] failed:', e);
        }
    }

    private _sendPageView(urlPath: string, browserUrl: string, properties?: MixpanelOptionalProperties): void {
        this._mixpanelService.registerPageUrl(browserUrl);
        this._mixpanelService.trackEvent('d&_page_view', {
            ...this._properties,
            ...properties,
            'URL Path': urlPath,
            event_timestamp: Date.now(),
        }, { ...this._identity! });
    }

    private _flushPendingPageView(): void {
        if (this._pendingPageView && this._identity) {
            this._sendPageView(
                this._pendingPageView.urlPath,
                this._pendingPageView.browserUrl,
                this._pendingPageView.properties
            );
            this._pendingPageView = null;
        }
    }
}

export default new RmsMixpanelStore(); 